// src/components/sections/BestSellersSection.jsx
import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { areFilterIdsEqual } from "utils/filterUtils";

import { ListProduct } from "..";
import { fetchBestSellers } from "../../redux/slices/bestSellersSlice";
import { STATUS } from "../../redux/status";

import ProductCarousel from "./ProductCarousel";

/**
 * The "Popular right now / Best sellers" rail — NetSuite's invoiced sales
 * (`fetchBestSellers` → `GET_TOP_SALE` → `salesInvoiced`, ranked by total invoiced
 * amount over a rolling **180-day** window, decided with the user).
 *
 * A standalone sibling of `RecentlyViewedSection`, not a mode of it — the landing
 * composes both (recents first, this under it), the PDP mounts the recents rail
 * alone. Split on request: the two rails are independent sections that happen to
 * sit together on one page.
 *
 * Still coupled to the recents SLICE on purpose: products already in the visitor's
 * recently-viewed history are filtered out, otherwise the top seller someone just
 * viewed renders twice within a screen of itself. That reads from
 * `state.recentViews` directly, so the section stays drop-in anywhere.
 *
 * The window is computed at dispatch, not hardcoded: the slice's old fixed
 * `fromDate: "2025-01-01"` was silently ageing into an all-time list. Renders
 * nothing while there is nothing to show — an empty rail is worse than none.
 *
 * (This file's previous life was the legacy `TitleSection` + `BlueBanner` version,
 * retired with the landing rebuild; the name was revived for the DS rail.)
 */
const BEST_SELLER_COUNT = 12;
const BEST_SELLER_WINDOW_DAYS = 180;

const rollingFromDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - BEST_SELLER_WINDOW_DAYS);
  return date.toISOString().slice(0, 10);
};

export default function BestSellersSection() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const bestSellers = useSelector((state) => state.bestSellers);
  const viewedProductIds = useSelector(
    (state) => state.recentViews.viewedProductIds
  );

  useEffect(() => {
    if (bestSellers.status === STATUS.IDLE) {
      dispatch(
        fetchBestSellers({
          limit: BEST_SELLER_COUNT,
          fromDate: rollingFromDate(),
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loading = bestSellers.status === STATUS.LOADING;
  const items = (bestSellers.items || []).filter(
    (item) =>
      !viewedProductIds.some((viewedId) => areFilterIdsEqual(viewedId, item.id))
  );

  if (items.length === 0 && !loading) {
    return null;
  }

  return (
    <ProductCarousel
      eyebrow="Popular right now"
      title="Best sellers"
      items={items}
      loading={loading}
      error={bestSellers.error}
      viewAllText="Browse the catalogue"
      onViewAll={() => navigate("/products")}
      renderItem={(item) => <ListProduct product={item} listType="grid" />}
    />
  );
}
